import { useEffect, useRef, useState } from 'react'
import apiClient from '../../../api/index.js'
import { identityService } from '../../../shared/api'
import { asMessage } from '../../../shared/utils/errorDetail'

/**
 * PLATFORM ADMIN: one company's AI Office Worker, as a guided checklist a competent NON-programmer can
 * follow (owner, 30 Sep 2026: "while the people who will handle this screen will be competent, they will
 * likely not be programmers"). Identity routers/platform_office_worker.py is the authority; this screen
 * only asks it.
 *
 *   * ONE STATUS LINE: Ready / Not set up yet - N steps left / Paused / Not available (server or company).
 *   * THE CHECKLIST, each step with its own button, so nobody hunts through other panels:
 *       - Available on this server, Company account active: read-only.
 *       1 Turn on the Office Worker - the SAME module-switch call the Modules panel makes
 *         (identityService.setCompanyModules, office_worker); its refusals are the server's words.
 *       2 AI writing - the SAME features call the Add-ons panel makes (office_worker_ai). OPTIONAL: it
 *         never holds "Ready" back (the server marks the gate optional).
 *       3 What the Office Worker may do - plain tick boxes; the words come from the server
 *         (shared/agent_host/scopes.py LABELS/DESCRIPTIONS), so a new permission arrives with its words.
 *         The one that emails CUSTOMERS sits apart, in red, and is never in "Recommended to start". Saving
 *         with at least one ticked allows it; "Pause" switches it off and keeps the ticks.
 *       4 Create the connection key - ONE button: the key gets exactly the SAVED permissions ("Advanced:
 *         limit this key" keeps the per-key subset for support).
 *   * CONNECTION KEYS: name, created by and when, last used, status; "Turn off this key" asks first.
 *   * EVERY CHANGE SAYS WHY: required on this screen; the permission and key routes also refuse without
 *     it (the module-switch and add-on routes take it as an optional note). One box at the top, with
 *     quick-pick answers that fill it, CLEARED after every change that succeeds so one reason is never
 *     recorded on the next change (security review, minor 2); turning a key off has its own box.
 *   * "Show technical names" (off by default) shows the codes beside the words, for support.
 *
 * INSIDE TYNVARA (owner, 30 Sep 2026; the server says `runs_inside`, read from shared/agent_host/mode.py):
 * the Office Worker reaches Tynvara in-process, gated by the company's permissions, with no key. Then there
 * is NO step 4, "Ready" needs no key (the server leaves the key gate out), the Connection keys list shows
 * only under "Show technical names", read-only, saying keys are for an Office Worker outside Tynvara, and
 * the one-time sign-in permission is shown only while it is still ticked (so it can be unticked); "Ready"
 * says to open it from the Office Worker tab (/office-worker).
 *
 * THE OFFICE WORKER'S OWNER (owner, 30 Sep 2026: the panel should finish the set-up). Inside Tynvara, step 3
 * also asks who OWNS the Office Worker at the company - one of its active admins, from the server's `admins`
 * list - and "Save and allow" needs one (the server refuses without it too). The Office Worker then sets
 * itself up for that person the first time anyone at the company opens it: no shell step. The server's
 * readiness carries an "owner" gate inside Tynvara, so "Ready" waits for it. ONCE IT IS SET UP (the server's
 * `registered`, written back by the Office Worker) the picker gives way to the owner it was set up for and
 * one line: after set-up its owner is changed under Access in the Office Worker (security review, 30 Sep).
 *
 * THE KEY IS SHOWN ONCE. It comes back once, is shown once in a copy box, and lives only in this
 * component's state: "Done" clears it, and closing the panel unmounts it (and the effect below clears it
 * on the way out), so reopening never shows it again. It is never put in a query cache, the page's state
 * or storage. While any request is in flight the page is told (onBusyChange) and will not close the panel,
 * so the answer to "Create the connection key" - the only copy of a live key - can never land after the
 * panel has gone.
 */
export const ONE_TIME_WORDS = 'Give this key to whoever runs the Office Worker service - it goes into that '
  + 'service\'s settings. Treat it like a password: anyone who has it can act as the Office Worker for this '
  + 'company. It will not be shown again.'
export const MAYBE_ISSUED_WORDS = 'The key may have been created. Check "Connection keys" below and turn off '
  + 'any key you did not receive.'
// Creating a key gets longer than the identity client's 10-second default: a slow answer must not be
// cut off and reported as a failure while the server is still committing a live key.
export const ISSUE_TIMEOUT_MS = 30000
export const CLIPBOARD_WORDS = 'Copying puts the key on this computer\'s clipboard, and clipboard history or sync '
  + '(Windows Win+V, a phone or cloud clipboard) may keep it. Clear it once the key is stored.'
export const REASON_PICKS = [
  'Testing on staging',
  'The company signed up',
  'The company asked for a change',
  'A key may have been exposed',
]
export const SAY_WHY_WORDS = 'Say why first: fill in "Why are you making this change?" at the top, or press '
  + 'one of its quick answers.'
export const SERVER_OFF_WORDS = 'The Office Worker is not switched on for this server. Ask your Tynvara '
  + 'technical contact.'
export const OWNER_WORDS = 'Choose who owns the Office Worker at this company - one of its admins - then save.'
export const CHANGE_OWNER_WORDS = 'To change the Office Worker\'s owner after set-up, use Access in the Office Worker.'
export const OWNER_HINT = 'The Office Worker sets itself up for this person the first time anyone at the company '
  + 'opens it. They then decide who else uses it. Changing it here later does not change the owner of an '
  + 'Office Worker already set up.'

const MODULE = 'office_worker'
const AI_FLAG = 'office_worker_ai'

const errorText = (err, fallback) => asMessage(err?.response?.data?.detail) || fallback

const when = (iso) => {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

const today = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const sameSet = (a, b) => a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',')

const listOf = (names) => (names.length <= 1
  ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`)

function Code({ show, children }) {
  if (!show) return null
  return <span className="ml-2 font-mono text-xs text-gray-400">({children})</span>
}

function Badge({ tone, children }) {
  const tones = {
    done: 'bg-green-100 text-green-800',
    todo: 'bg-amber-100 text-amber-800',
    skip: 'bg-gray-100 text-gray-700',
    off: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${tones[tone]}`}>{children}</span>
  )
}

function Message({ msg, at }) {
  if (!msg || msg.at !== at) return null
  return (
    <p role="status" className={`text-sm ${msg.type === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>
  )
}

function ReasonBox({ label, value, onChange, busy, inputRef }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-800">
        {label}
        <span className="block text-xs font-normal text-gray-500">
          Required. It is kept with the record of the change, with your name.
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          maxLength={500}
          onChange={(e) => onChange(e.target.value)}
          disabled={busy}
          aria-label={label}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        {REASON_PICKS.map((r) => (
          <button key={r} type="button" onClick={() => onChange(r)} disabled={busy}
            className="text-xs px-2 py-1 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {r}
          </button>
        ))}
      </div>
    </div>
  )
}

function Step({ number, title, badge, children, testId }) {
  return (
    <li className="p-3 space-y-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <h5 className="font-medium text-gray-900">
          {number ? <span className="text-gray-500 mr-1">{number}.</span> : null}
          {title}
        </h5>
        {badge}
      </div>
      {children}
    </li>
  )
}

export default function TenantOfficeWorker({ tenantId, onKeyShowingChange, onBusyChange }) {
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [msg, setMsg] = useState(null)             // { type, text, at: the step it belongs to }
  const [busy, setBusy] = useState(false)
  const [showTech, setShowTech] = useState(false)

  const [reason, setReason] = useState('')
  const reasonRef = useRef(null)

  // What the Office Worker may do - the draft: null = as saved.
  const [draft, setDraft] = useState(null)
  // Inside Tynvara: the Office Worker's owner - the draft: null = as saved.
  const [ownerDraft, setOwnerDraft] = useState(null)

  const [keyName, setKeyName] = useState(null)     // null = the suggested name
  const [keyLimit, setKeyLimit] = useState(null)   // null = every saved permission
  // The one-time key. Only ever here.
  const [newKey, setNewKey] = useState(null)
  const [copied, setCopied] = useState(false)

  const [turningOff, setTurningOff] = useState(null)   // the key row being turned off
  const [turnOffReason, setTurnOffReason] = useState('')

  const keyShowing = Boolean(newKey)
  const reportRef = useRef(onKeyShowingChange)
  reportRef.current = onKeyShowingChange
  useEffect(() => { reportRef.current?.(keyShowing) }, [keyShowing])
  // A request in flight - creating a key above all, whose answer is the only copy of a live key - holds
  // the panel open: the page will not close it until this settles (security review of b9958597).
  const busyRef = useRef(onBusyChange)
  busyRef.current = onBusyChange
  useEffect(() => { busyRef.current?.(busy) }, [busy])
  // Closing the panel unmounts this: forget the key and tell the page nothing is showing or running.
  useEffect(() => () => { setNewKey(null); reportRef.current?.(false); busyRef.current?.(false) }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiClient.get(`/platform/office-worker/${tenantId}`)
        if (!cancelled) setData(res.data)
      } catch (err) {
        if (!cancelled) setLoadError(errorText(err, 'Could not load this company\'s Office Worker.'))
      }
    })()
    return () => { cancelled = true }
  }, [tenantId])

  if (loadError) return <p className="text-sm text-red-600">{loadError}</p>
  if (!data) return <p className="text-sm text-gray-500">Loading the Office Worker…</p>

  const gate = Object.fromEntries(data.readiness.gates.map((g) => [g.key, g]))
  const isOn = (k) => gate[k]?.on === true
  const serverOn = isOn('platform_switch')
  const active = isOn('company_active')
  const moduleOn = isOn('module_switch')
  const aiOn = isOn('entitlement')
  const liveKey = isOn('live_key')
  const inside = data.runs_inside === true

  const saved = data.grant || { enabled: false, scopes: [] }
  const allowed = saved.enabled === true
  const paused = Boolean(data.grant) && !allowed
  const perms = data.scopes
  const labelOf = (id) => perms.find((p) => p.id === id)?.label || id
  const chosen = draft === null ? saved.scopes : draft
  const permsDirty = !sameSet(chosen, saved.scopes)
  // The owner (inside Tynvara only). One who is no longer an active admin is not offered as chosen.
  const admins = data.admins || []
  const owner = data.owner || null
  const registered = inside ? data.registered || null : null
  const ownerOk = !inside || isOn('owner')
  const savedOwnerRef = owner?.is_admin ? owner.user_ref : ''
  const ownerChoice = ownerDraft === null ? savedOwnerRef : ownerDraft
  const ownerDirty = inside && !registered && ownerChoice !== '' && ownerChoice !== (owner?.user_ref || '')
  const stepsLeft = [!moduleOn, !allowed || !ownerOk, !inside && !liveKey].filter(Boolean).length

  const companyName = data.company.name
  const nameValue = keyName === null ? `Office Worker - ${companyName} - ${today()}` : keyName
  const keyPerms = keyLimit === null ? saved.scopes : keyLimit.filter((s) => saved.scopes.includes(s))
  // WORKING = not turned off AND able to do something that is saved - the server's rule for the step-4
  // gate. Taking a permission away narrows every key, possibly to nothing (security review, minor 1).
  const canWork = (k) => !k.revoked_at && k.scopes.some((s) => saved.scopes.includes(s))
  const liveKeys = data.keys.filter(canWork)
  const onlyStaleKeys = !liveKey && data.keys.some((k) => !k.revoked_at)
  const missingFromKeys = liveKeys.length
    ? saved.scopes.filter((s) => !liveKeys.some((k) => k.scopes.includes(s)))
    : []

  let status
  let ready = false
  if (!serverOn) status = { tone: 'text-red-700', text: `Not available: ${SERVER_OFF_WORDS}` }
  else if (!active) status = { tone: 'text-red-700', text: 'Not available: this company\'s account is suspended.' }
  else if (paused) {
    status = { tone: 'text-amber-700',
      text: 'Paused: the Office Worker is paused for this company. Its choices are kept - press "Allow again" in step 3 to start it again.' }
  } else if (data.readiness.ready) {
    ready = true
    status = { tone: 'text-green-700', text: 'Ready: the Office Worker can work for this company.' }
  }
  else status = { tone: 'text-amber-700', text: `Not set up yet - ${stepsLeft} ${stepsLeft === 1 ? 'step' : 'steps'} left.` }

  const reload = async () => {
    const res = await apiClient.get(`/platform/office-worker/${tenantId}`)
    setData(res.data)
    setDraft(null)
    setOwnerDraft(null)
  }

  const refreshFrom = (body) => {
    setData(body)
    setDraft(null)
    setOwnerDraft(null)
  }

  // Every change carries the reason; without one, nothing is sent and the person is shown where to say it.
  const needReason = (at) => {
    if (reason.trim()) return false
    setMsg({ type: 'err', text: SAY_WHY_WORDS, at })
    reasonRef.current?.focus()
    return true
  }

  const run = async (at, fn, okText, failText) => {
    setMsg(null)
    if (needReason(at)) return
    setBusy(true)
    try {
      await fn()
      setReason('')
      setMsg({ type: 'ok', text: okText, at })
    } catch (err) {
      setMsg({ type: 'err', text: errorText(err, failText), at })
    } finally {
      setBusy(false)
    }
  }

  const turnOnModule = () => run('module', async () => {
    await identityService.setCompanyModules(tenantId, { [MODULE]: true }, reason.trim())
    await reload()
  }, 'Turned on for this company, and recorded with your name.', 'The Office Worker was not turned on.')

  const setAi = (on) => run('ai', async () => {
    await apiClient.put(`/branding/${tenantId}/features`, { features: { [AI_FLAG]: on }, note: reason.trim() })
    await reload()
  }, on ? 'AI writing turned on, and recorded with your name.' : 'AI writing turned off, and recorded with your name.',
  'AI writing was not changed.')

  const savePerms = () => {
    if (chosen.length === 0) {
      setMsg({ type: 'err',
        text: `Tick at least one thing the Office Worker may do.${allowed ? ' To stop it, press "Pause the Office Worker for this company" instead.' : ''}`,
        at: 'perms' })
      return
    }
    // Inside Tynvara, allowing it needs its owner: the Office Worker sets itself up for that person.
    if (inside && !registered && !ownerChoice) {
      setMsg({ type: 'err', text: OWNER_WORDS, at: 'perms' })
      return
    }
    const change = {}
    if (!allowed) change.enabled = true
    if (permsDirty) change.scopes = [...chosen].sort()
    if (ownerDirty) change.owner_user_ref = ownerChoice
    return run('perms', async () => {
      const res = await apiClient.put(`/platform/office-worker/${tenantId}/grant`, { ...change, reason: reason.trim() })
      refreshFrom(res.data)
    }, 'Saved, and recorded with your name.', 'Nothing was changed.')
  }

  const pause = () => run('perms', async () => {
    const res = await apiClient.put(`/platform/office-worker/${tenantId}/grant`, { enabled: false, reason: reason.trim() })
    refreshFrom(res.data)
  }, 'Paused, and recorded with your name. Its choices are kept.', 'Nothing was changed.')

  const createKey = async () => {
    setMsg(null)
    if (!nameValue.trim()) { setMsg({ type: 'err', text: 'Give the key a name.', at: 'key' }); return }
    if (keyPerms.length === 0) {
      setMsg({ type: 'err', text: 'The key must be allowed at least one thing.', at: 'key' })
      return
    }
    if (needReason('key')) return
    setBusy(true)
    try {
      const res = await apiClient.post(`/platform/office-worker/${tenantId}/keys`,
        { label: nameValue.trim(), scopes: [...keyPerms].sort(), reason: reason.trim() },
        { timeout: ISSUE_TIMEOUT_MS })
      setNewKey(res.data.key)
      setCopied(false)
      refreshFrom(res.data.company)
      setReason('')
      setKeyName(null)
      setKeyLimit(null)
    } catch (err) {
      if (!err?.response) {
        // No answer (a timeout, a dropped connection): the server may well have committed the key
        // after we stopped listening. Never say "not created" then - say what to check, and show it.
        setMsg({ type: 'err', text: MAYBE_ISSUED_WORDS, at: 'key' })
        try {
          await reload()
        } catch {
          // The list could not be reloaded either; the words above still say what to do.
        }
      } else {
        setMsg({ type: 'err', text: errorText(err, 'No key was created.'), at: 'key' })
      }
    } finally {
      setBusy(false)
    }
  }

  const turnOffKey = async () => {
    setMsg(null)
    if (!turnOffReason.trim()) { setMsg({ type: 'err', text: 'Say why you are turning this key off.', at: 'keys' }); return }
    setBusy(true)
    try {
      const res = await apiClient.post(
        `/platform/office-worker/${tenantId}/keys/${turningOff.key_id}/revoke`, { reason: turnOffReason.trim() })
      refreshFrom(res.data)
      setTurningOff(null)
      setTurnOffReason('')
      setMsg({ type: 'ok', text: 'Key turned off, and recorded with your name. The Office Worker using it is refused from now on.', at: 'keys' })
    } catch (err) {
      setMsg({ type: 'err', text: errorText(err, 'The key was not turned off.'), at: 'keys' })
    } finally {
      setBusy(false)
    }
  }

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey)
      setCopied(true)
    } catch {
      setCopied(false)
      setMsg({ type: 'err', text: 'Could not copy. Select the key and copy it by hand.', at: 'newkey' })
    }
  }

  const toggle = (id) => setDraft(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id])
  const recommended = perms.filter((p) => p.recommended && !p.emails_customers).map((p) => p.id)
  // Inside Tynvara a permission not in use there (the one-time sign-in) is offered only while still ticked.
  const shown = perms.filter((p) => p.in_use !== false || chosen.includes(p.id))
  const staffPerms = shown.filter((p) => !p.emails_customers)
  const customerPerms = shown.filter((p) => p.emails_customers)

  const permBox = (p) => (
    <li key={p.id} className="flex items-start gap-2">
      <input
        id={`ow-perm-${p.id}`}
        type="checkbox"
        checked={chosen.includes(p.id)}
        onChange={() => toggle(p.id)}
        disabled={busy}
        aria-label={p.label}
        className="mt-1"
      />
      <label htmlFor={`ow-perm-${p.id}`} className="text-sm">
        <span className={`font-medium ${p.emails_customers ? 'text-red-800' : 'text-gray-900'}`}>{p.label}</span>
        <Code show={showTech}>{p.id}</Code>
        <span className={`block text-xs ${p.emails_customers ? 'text-red-700' : 'text-gray-500'}`}>{p.description}</span>
        {p.in_use === false && (
          <span className="block text-xs text-amber-700">Not used while the Office Worker runs inside Tynvara - you can untick it.</span>
        )}
      </label>
    </li>
  )

  const primary = 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg'
  const secondary = 'text-sm font-medium px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50'

  return (
    <div className="space-y-6">
      {/* ── Status ── */}
      <section aria-label="Status" className="space-y-1">
        <p className={`text-base font-semibold ${status.tone}`} data-testid="ow-status">{status.text}</p>
        {inside && ready && (
          <p className="text-sm text-gray-700">
            Open it from the <a href="/office-worker" className="text-blue-600 hover:text-blue-700 underline">Office Worker tab</a> in Tynvara&apos;s menu.
          </p>
        )}
        {inside && ready && registered && (
          <p className="text-xs text-gray-500" data-testid="ow-set-up-for">
            Set up {when(registered.at) ? `on ${when(registered.at)} ` : ''}for {registered.owner?.name || registered.owner?.email || 'its owner'}.
          </p>
        )}
        {inside && ready && !registered && owner && (
          <p className="text-xs text-gray-500" data-testid="ow-sets-itself-up">
            It sets itself up for {owner.name || owner.email || 'its owner'} the first time anyone at the company opens it.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Follow the steps below in order. Every change is recorded with your name and your reason.
        </p>
      </section>

      <section aria-label="Why" className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <ReasonBox label="Why are you making this change?" value={reason} onChange={setReason} busy={busy}
          inputRef={reasonRef} />
      </section>

      {/* ── The checklist ── */}
      <ol className="divide-y divide-gray-100 border border-gray-200 rounded-lg" data-testid="ow-steps">
        <Step title="Available on this server" testId="ow-step-server"
          badge={serverOn ? <Badge tone="done">Yes</Badge> : <Badge tone="off">No</Badge>}>
          <p className="text-xs text-gray-500">
            {serverOn ? 'The Office Worker is switched on for this server.' : SERVER_OFF_WORDS}
            <Code show={showTech}>OFFICE_WORKER_ENABLED</Code>
          </p>
        </Step>

        <Step title="Company account active" testId="ow-step-company"
          badge={active ? <Badge tone="done">Yes</Badge> : <Badge tone="off">Suspended</Badge>}>
          <p className="text-xs text-gray-500">
            {active
              ? 'This company\'s account is active.'
              : 'This company\'s account is suspended, so the Office Worker is refused everything and no key can be created. Reactivate the company first (the Reactivate button on its row).'}
          </p>
        </Step>

        <Step number={1} title="Turn on the Office Worker for this company" testId="ow-step-module"
          badge={moduleOn ? <Badge tone="done">Done</Badge> : <Badge tone="todo">Not done</Badge>}>
          {moduleOn ? (
            <p className="text-xs text-gray-500">
              Done: it is turned on for this company.<Code show={showTech}>{MODULE}</Code>
            </p>
          ) : (
            <>
              {(() => {
                const needs = gate.module_switch?.needs || []
                const names = needs.length ? needs.map((n) => n.label) : ['Projects', 'Timekeeping']
                const off = needs.filter((n) => !n.on).map((n) => n.label)
                return (
                  <p className={`text-xs ${off.length ? 'text-amber-700' : 'text-gray-500'}`} data-testid="ow-module-needs">
                    It needs {listOf(names)} switched on for this company.
                    {off.length > 0 && ` ${listOf(off)} ${off.length === 1 ? 'is' : 'are'} switched off, so this will be refused - switch ${off.length === 1 ? 'it' : 'them'} on under Modules first.`}
                  </p>
                )
              })()}
              <div className="flex justify-end">
                <button type="button" onClick={turnOnModule} disabled={busy} className={primary}>Turn on</button>
              </div>
            </>
          )}
          <Message msg={msg} at="module" />
        </Step>

        <Step number={2} title="AI writing (paid add-on, optional)" testId="ow-step-ai"
          badge={aiOn ? <Badge tone="done">On</Badge> : <Badge tone="skip">Skipped</Badge>}>
          <p className="text-xs text-gray-500">
            Without it the Office Worker writes from fixed templates only; with it, it may use AI once the
            company&apos;s own admin also agrees to AI in their settings.<Code show={showTech}>{AI_FLAG}</Code>
          </p>
          <div className="flex justify-end">
            {aiOn ? (
              <button type="button" onClick={() => setAi(false)} disabled={busy} className={secondary}>Turn off AI writing</button>
            ) : (
              <button type="button" onClick={() => setAi(true)} disabled={busy} className={secondary}>Turn on AI writing</button>
            )}
          </div>
          <Message msg={msg} at="ai" />
        </Step>

        <Step number={3} title={inside ? 'Choose what the Office Worker may do, and who owns it' : 'Choose what the Office Worker may do'}
          testId="ow-step-perms"
          badge={allowed && ownerOk ? <Badge tone="done">Done</Badge> : paused ? <Badge tone="todo">Paused</Badge> : <Badge tone="todo">Not done</Badge>}>
          <p className="text-xs text-gray-500">
            {allowed && ownerOk && 'Done: it may do what is ticked below.'}
            {allowed && !ownerOk && 'Almost: choose who owns the Office Worker below, then save.'}
            {paused && 'Paused: it may do nothing right now. The ticks are kept - press "Allow again" to start it again.'}
            {!data.grant && 'Tick what it may do, then press "Save and allow". Not sure? Press "Recommended to start".'}
          </p>
          <div>
            <button type="button" onClick={() => setDraft(recommended)} disabled={busy} className={secondary}>
              Recommended to start
            </button>
          </div>
          <ul className="space-y-2" data-testid="ow-perms-inside">{staffPerms.map(permBox)}</ul>
          {customerPerms.length > 0 && (
            <div className="border-2 border-red-200 bg-red-50 rounded-lg p-3 space-y-2" data-testid="ow-perms-outside">
              <p className="text-xs font-semibold text-red-800">
                Reaches people OUTSIDE this company. Only when the company has asked for it - never part of
                &quot;Recommended to start&quot;.
              </p>
              <ul className="space-y-2">{customerPerms.map(permBox)}</ul>
            </div>
          )}
          <p className="text-xs text-gray-500">Taking a tick away also takes it away from every key straight away.</p>
          {inside && registered && (
            <div className="space-y-1" data-testid="ow-owner-set-up">
              <p className="text-sm text-gray-800">
                Office Worker owner: <span className="font-medium">{registered.owner?.name || registered.owner?.email || 'not recorded'}</span>
                {registered.owner?.email && registered.owner?.name ? <span className="text-gray-500"> ({registered.owner.email})</span> : null}
              </p>
              <p className="text-xs text-gray-500">{CHANGE_OWNER_WORDS}</p>
              <Code show={showTech}>{registered.owner?.user_ref || 'no owner'}</Code>
            </div>
          )}
          {inside && !registered && (
            <div className="space-y-1" data-testid="ow-owner">
              <label htmlFor="ow-owner-pick" className="block text-sm font-medium text-gray-800">
                Office Worker owner
                <span className="block text-xs font-normal text-gray-500">{OWNER_HINT}</span>
              </label>
              {admins.length === 0 ? (
                <p className="text-xs text-amber-700">
                  This company has no active admin yet. Make someone an admin of the company first.
                </p>
              ) : (
                <select id="ow-owner-pick" value={ownerChoice} onChange={(e) => setOwnerDraft(e.target.value)}
                  disabled={busy} aria-label="Office Worker owner"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">Choose one of the company&apos;s admins…</option>
                  {admins.map((a) => (
                    <option key={a.user_ref} value={a.user_ref}>{a.name}{a.email ? ` (${a.email})` : ''}</option>
                  ))}
                </select>
              )}
              {owner && !owner.is_admin && (
                <p className="text-xs text-amber-700" data-testid="ow-owner-not-admin">
                  {owner.name || owner.email || 'The chosen owner'} is no longer an active admin of this company. Choose another.
                </p>
              )}
              <Code show={showTech}>{owner?.user_ref || 'no owner'}</Code>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {allowed && (
              <button type="button" onClick={pause} disabled={busy}
                className="text-sm font-medium px-3 py-2 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                Pause the Office Worker for this company
              </button>
            )}
            <button type="button" onClick={savePerms} disabled={busy || (allowed && !permsDirty && !ownerDirty)} className={primary}>
              {allowed ? (permsDirty || ownerDirty ? 'Save changes' : 'Saved') : paused ? 'Allow again' : 'Save and allow'}
            </button>
          </div>
          <Message msg={msg} at="perms" />
        </Step>

        {!inside && (
          <Step number={4} title="Create the connection key" testId="ow-step-key"
            badge={liveKey ? <Badge tone="done">Done</Badge> : <Badge tone="todo">Not done</Badge>}>
            <p className="text-xs text-gray-500">
              {liveKey
                ? 'Done: a connection key is working. Create another only to replace it.'
                : 'The Office Worker service needs this key to work for this company.'}
            </p>
            {!allowed ? (
              <p className="text-xs text-amber-700">First save what the Office Worker may do (step 3).</p>
            ) : !active ? (
              <p className="text-xs text-amber-700">This company&apos;s account is suspended, so no key can be created.</p>
            ) : (
              <>
                {permsDirty && (
                  <p className="text-xs text-amber-700">You have unsaved changes in step 3. Save them first: the key gets what is saved.</p>
                )}
                {onlyStaleKeys && (
                  <p className="text-xs text-amber-700">The working key can do nothing that is now allowed. Create a new key.</p>
                )}
                {missingFromKeys.length > 0 && (
                  <p className="text-xs text-amber-700" data-testid="ow-key-behind">
                    The working key was made before you allowed: {missingFromKeys.map(labelOf).join('; ')}. It cannot
                    do that. Create a new key, give it to the Office Worker service, then turn off the old one.
                  </p>
                )}
                <label className="block text-sm text-gray-700">
                  Name for this key
                  <input type="text" value={nameValue} maxLength={120} onChange={(e) => setKeyName(e.target.value)}
                    disabled={busy} aria-label="Name for this key"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </label>
                <div className="text-xs text-gray-600" data-testid="ow-key-gets">
                  The key will be allowed to do exactly what is saved in step 3:
                  <ul className="mt-1 list-disc pl-5">
                    {keyPerms.map((s) => <li key={s}>{labelOf(s)}<Code show={showTech}>{s}</Code></li>)}
                  </ul>
                </div>
                <details className="text-xs text-gray-600">
                  <summary className="cursor-pointer">Advanced: limit this key</summary>
                  <p className="mt-1">For support only. Untick anything this one key should not do.</p>
                  <ul className="mt-1 space-y-1">
                    {saved.scopes.map((s) => (
                      <li key={s}>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={keyPerms.includes(s)} disabled={busy}
                            aria-label={`${labelOf(s)} (this key only)`}
                            onChange={() => setKeyLimit(keyPerms.includes(s) ? keyPerms.filter((x) => x !== s) : [...keyPerms, s])} />
                          {labelOf(s)}<Code show={showTech}>{s}</Code>
                        </label>
                      </li>
                    ))}
                  </ul>
                </details>
                <div className="flex justify-end">
                  <button type="button" onClick={createKey} disabled={busy || permsDirty} className={primary}>
                    {liveKey ? 'Create another key' : 'Create the connection key'}
                  </button>
                </div>
              </>
            )}
            <Message msg={msg} at="key" />

            {/* ── The one-time key ── */}
            {newKey && (
              <section className="border-2 border-amber-400 bg-amber-50 rounded-lg p-3 space-y-2" aria-label="New key">
                <p className="text-sm font-semibold text-amber-900">{ONE_TIME_WORDS}</p>
                <p className="text-xs text-amber-900">{CLIPBOARD_WORDS}</p>
                <textarea
                  readOnly
                  value={newKey}
                  rows={2}
                  onFocus={(e) => e.target.select()}
                  aria-label="The new key"
                  className="w-full font-mono text-xs border border-amber-300 rounded p-2 bg-white"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={copyKey}
                    className="text-sm px-3 py-1.5 rounded-lg border border-amber-400 text-amber-900 hover:bg-amber-100">
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button type="button" onClick={() => { setNewKey(null); setCopied(false) }}
                    className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700">
                    Done - I have stored it
                  </button>
                </div>
                <Message msg={msg} at="newkey" />
              </section>
            )}
          </Step>
        )}
      </ol>

      {/* ── Connection keys (inside Tynvara: only under technical names, read-only) ── */}
      {(!inside || showTech) && (
        <section aria-labelledby="ow-keys" className="space-y-2">
          <h4 id="ow-keys" className="font-semibold text-gray-900">Connection keys</h4>
          {inside && (
            <p className="text-xs text-gray-500">
              Keys are only for an Office Worker running outside Tynvara. This one runs inside Tynvara and uses none.
            </p>
          )}
          {data.keys.length === 0 ? (
            <p className="text-sm text-gray-500">No keys yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1 pr-2">Name</th>
                    <th className="py-1 pr-2">Created</th>
                    <th className="py-1 pr-2">Last used</th>
                    <th className="py-1 pr-2">Status</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.keys.map((k) => (
                    <tr key={k.key_id}>
                      <td className="py-1 pr-2 align-top">
                        <div>{k.label || 'Unnamed key'}</div>
                        <div className="text-xs text-gray-500">
                          May: {k.scopes.length ? k.scopes.map(labelOf).join('; ') : 'nothing'}
                        </div>
                        {showTech && (
                          <div className="font-mono text-xs text-gray-400">{k.key_id} ({k.scopes.join(', ')})</div>
                        )}
                      </td>
                      <td className="py-1 pr-2 text-xs align-top">{when(k.created_at)}<div className="text-gray-400">by {k.created_by}</div></td>
                      <td className="py-1 pr-2 text-xs align-top">{k.last_used_at ? when(k.last_used_at) : 'Never'}</td>
                      <td className="py-1 pr-2 text-xs align-top">{k.revoked_at ? `Turned off ${when(k.revoked_at)}` : canWork(k) ? 'Working' : 'Can do nothing now'}</td>
                      <td className="py-1 text-right align-top">
                        {!k.revoked_at && !inside && (
                          <button type="button" onClick={() => { setTurningOff(k); setTurnOffReason(''); setMsg(null) }}
                            disabled={busy}
                            aria-label={`Turn off ${k.label || 'this key'}`}
                            className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                            Turn off this key
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {turningOff && !inside && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-2" role="dialog" aria-label="Turn off key">
              <p className="text-sm text-red-800">
                Turn off &quot;{turningOff.label || 'this key'}&quot;? The Office Worker using it is refused from its
                next request. This cannot be undone: you would create a new key instead.
              </p>
              <ReasonBox label="Why are you turning off this key?" value={turnOffReason} onChange={setTurnOffReason} busy={busy} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setTurningOff(null)} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded-lg text-gray-700 hover:bg-gray-200">Cancel</button>
                <button type="button" onClick={turnOffKey} disabled={busy || !turnOffReason.trim()}
                  className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                  Yes, turn it off
                </button>
              </div>
            </div>
          )}
          <Message msg={msg} at="keys" />
        </section>
      )}

      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-xs text-gray-500">
          <input type="checkbox" checked={showTech} onChange={() => setShowTech(!showTech)} aria-label="Show technical names" />
          Show technical names
        </label>
      </div>
    </div>
  )
}
